import { environment } from '@environment';

/** `global_feature_switches.feature_name`; `switch_value` true → web app uses group-order v3, false → v1. */
export const GROUP_ORDER_REVAMP_V2_SWITCH = 'GROUP_ORDER_REVAMP_V2';

/** Single-flag stores route; use with `{adminV3Url}` (trailing slash ok). */
export const GROUP_ORDER_WEB_FEATURE_API_PATH = 'api/stores/group-order-web-feature';
const GROUP_ORDER_VERSION_CACHE_KEY = 'fp:group-order-web-feature';
const GROUP_ORDER_VERSION_CACHE_TTL_MS = 5 * 60 * 1000;
const GROUP_ORDER_FEATURE_FETCH_PROMISE_KEY = '__fpGroupOrderFeatureFetchPromise';
/**
 * Long-lived (30 minutes) cache used in addition to the per-tab session cache.
 * Cuts the bootstrap critical chain by one round-trip on repeat visits across tabs.
 * The session cache still wins when present (it's authoritative for the current tab).
 */
const GROUP_ORDER_VERSION_LOCAL_TTL_MS = 30 * 60 * 1000;

type GroupOrderVersion = 'v1' | 'v3';
type GroupOrderFeatureFetchPromise = Promise<unknown>;

type CachedGroupOrderVersion = {
  version: GroupOrderVersion;
  expiresAt: number;
};

declare global {
  interface Window {
    [GROUP_ORDER_FEATURE_FETCH_PROMISE_KEY]?: GroupOrderFeatureFetchPromise;
  }
}

function isSwitchOn(raw: unknown): boolean {
  return raw === true || raw === 1 || raw === '1';
}

/** Response from `GET /api/stores/group-order-web-feature` (stores envelope). */
function parseStoresGroupOrderFeatureBody(body: unknown): unknown | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return undefined;
  }
  const o = body as Record<string, unknown>;
  if (o.success !== true || o.data == null || typeof o.data !== 'object' || Array.isArray(o.data)) {
    return undefined;
  }
  const data = o.data as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(data, GROUP_ORDER_REVAMP_V2_SWITCH)) {
    return undefined;
  }
  return data[GROUP_ORDER_REVAMP_V2_SWITCH];
}

function featureUrl(): string {
  return `${environment.adminV3Url}${GROUP_ORDER_WEB_FEATURE_API_PATH}`;
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readCachedFromStore(store: Storage | undefined): GroupOrderVersion | null {
  if (!store) {
    return null;
  }
  try {
    const raw = store.getItem(GROUP_ORDER_VERSION_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedGroupOrderVersion;
    if (!parsed || (parsed.version !== 'v1' && parsed.version !== 'v3')) {
      return null;
    }
    if (!Number.isFinite(parsed.expiresAt) || parsed.expiresAt <= Date.now()) {
      store.removeItem(GROUP_ORDER_VERSION_CACHE_KEY);
      return null;
    }
    return parsed.version;
  } catch {
    return null;
  }
}

function readCachedGroupOrderVersion(): GroupOrderVersion | null {
  return (
    readCachedFromStore(canUseSessionStorage() ? window.sessionStorage : undefined) ??
    readCachedFromStore(canUseLocalStorage() ? window.localStorage : undefined)
  );
}

function writeCachedGroupOrderVersion(version: GroupOrderVersion): void {
  const now = Date.now();
  if (canUseSessionStorage()) {
    try {
      const payload: CachedGroupOrderVersion = {
        version,
        expiresAt: now + GROUP_ORDER_VERSION_CACHE_TTL_MS,
      };
      window.sessionStorage.setItem(GROUP_ORDER_VERSION_CACHE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore storage quota/security errors */
    }
  }
  if (canUseLocalStorage()) {
    try {
      const payload: CachedGroupOrderVersion = {
        version,
        expiresAt: now + GROUP_ORDER_VERSION_LOCAL_TTL_MS,
      };
      window.localStorage.setItem(GROUP_ORDER_VERSION_CACHE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore storage quota/security errors */
    }
  }
}

/**
 * Synchronously applies any cached `groupOrderVersion` so the very first `canMatch`
 * evaluation already sees the cached value (skips the network round-trip on repeat visits).
 * Safe to call before {@link prefetchGroupOrderVersionFromBackend} / Angular bootstrap.
 */
export function applyCachedGroupOrderVersion(): void {
  const cached = readCachedGroupOrderVersion();
  if (cached) {
    environment.groupOrderVersion = cached;
  }
}

function createGroupOrderFeatureFetchPromise(): GroupOrderFeatureFetchPromise {
  return fetch(featureUrl(), { credentials: 'include' }).then((res) => {
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    return res.json() as Promise<unknown>;
  });
}

function getSharedGroupOrderFeatureFetchPromise(): GroupOrderFeatureFetchPromise {
  if (typeof window === 'undefined') {
    return createGroupOrderFeatureFetchPromise();
  }
  const existing = window[GROUP_ORDER_FEATURE_FETCH_PROMISE_KEY];
  if (existing) {
    return existing;
  }
  const promise = createGroupOrderFeatureFetchPromise().catch((err) => {
    delete window[GROUP_ORDER_FEATURE_FETCH_PROMISE_KEY];
    throw err;
  });
  window[GROUP_ORDER_FEATURE_FETCH_PROMISE_KEY] = promise;
  return promise;
}

/**
 * Starts the stores feature request early so first bootstrap can await the same promise
 * instead of issuing a second network call.
 */
export function prefetchGroupOrderVersionFromBackend(): void {
  if (typeof window === 'undefined') {
    return;
  }
  void getSharedGroupOrderFeatureFetchPromise();
}

/**
 * Loads the group-order web feature flag before Angular bootstraps so lazy `loadChildren` sees the correct `environment.groupOrderVersion`.
 * Uses a dedicated stores endpoint (single DB row) instead of the full `api/fp/feature-switches` list.
 */
export async function loadGroupOrderVersionFromBackend(): Promise<void> {
  const cachedVersion = readCachedGroupOrderVersion();
  if (cachedVersion) {
    environment.groupOrderVersion = cachedVersion;
    return;
  }

  try {
    const body = await getSharedGroupOrderFeatureFetchPromise();
    const raw = parseStoresGroupOrderFeatureBody(body);

    if (raw === undefined) {
      if (!environment.production) {
        console.warn(
          '[group-order] stores/group-order-web-feature: unexpected response or missing key; keeping environment.groupOrderVersion.',
          body
        );
      }
      return;
    }
    if (raw === null) {
      return;
    }
    const resolvedVersion: GroupOrderVersion = isSwitchOn(raw) ? 'v3' : 'v1';
    environment.groupOrderVersion = resolvedVersion;
    writeCachedGroupOrderVersion(resolvedVersion);
  } catch (err) {
    if (!environment.production) {
      console.warn(
        '[group-order] stores/group-order-web-feature request failed; keeping environment.groupOrderVersion.',
        err
      );
    }
  }
}
